import {
  DEFAULT_UNKNOWN_SEO_RULE,
  GLOBAL_SEO_LOCKED_RULES,
  SEO_TEMPLATE_RULES,
} from "./seoRouteRules";
import { sanitizeAgentSeoOverride } from "./sanitizeAgentSeoOverride";

const INTERNAL_ORIGIN_FALLBACK = "http://localhost";
const REQUIRED_LOCKED_PATHS = ["/admin", "/dashboard", "/p", "/api"];

const normalizePathname = (pathname) => {
  if (!pathname || typeof pathname !== "string") {
    return "/";
  }
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return normalized.replace(/\/+$/, "") || "/";
};

const findFirstRuleMatch = (rules, pathname) => rules.find((rule) => rule.pattern.test(pathname));

const isRuleObject = (rule) =>
  Boolean(rule && rule.pattern instanceof RegExp && typeof rule.type === "string");

const hasRequiredLockedCoverage = (globalRules) =>
  REQUIRED_LOCKED_PATHS.every((path) =>
    globalRules.some((rule) => rule.locked === true && rule.disallow === true && rule.pattern.test(path))
  );

const resolveRuleSets = (runtimeRules) => {
  if (!runtimeRules || typeof runtimeRules !== "object") {
    return {
      globalRules: GLOBAL_SEO_LOCKED_RULES,
      templateRules: SEO_TEMPLATE_RULES,
    };
  }

  const globalRules = Array.isArray(runtimeRules.globalRules) ? runtimeRules.globalRules.filter(isRuleObject) : [];
  const templateRules = Array.isArray(runtimeRules.templateRules)
    ? runtimeRules.templateRules.filter(isRuleObject)
    : [];

  if (!globalRules.length || !templateRules.length || !hasRequiredLockedCoverage(globalRules)) {
    return {
      globalRules: GLOBAL_SEO_LOCKED_RULES,
      templateRules: SEO_TEMPLATE_RULES,
    };
  }

  return {
    globalRules,
    templateRules,
  };
};

const hasSearchParams = (searchParams) => {
  if (!searchParams) {
    return false;
  }
  if (typeof searchParams === "string") {
    return searchParams.replace(/^\?/, "").trim().length > 0;
  }
  if (searchParams instanceof URLSearchParams) {
    return Array.from(searchParams.keys()).length > 0;
  }
  if (typeof searchParams === "object") {
    return Object.keys(searchParams).length > 0;
  }
  return false;
};

const normalizeCanonicalPath = (canonicalUrl, origin) => {
  if (!canonicalUrl || typeof canonicalUrl !== "string") {
    return null;
  }

  if (canonicalUrl.startsWith("/")) {
    return canonicalUrl;
  }

  try {
    const safeOrigin = origin || INTERNAL_ORIGIN_FALLBACK;
    const parsed = new URL(canonicalUrl, safeOrigin);
    if (origin && parsed.origin !== origin) {
      return null;
    }
    return parsed.pathname || "/";
  } catch {
    return null;
  }
};

const toRobotsContent = ({ index, follow }) => `${index ? "index" : "noindex"}, ${follow ? "follow" : "nofollow"}`;

/**
 * @param {{
 * pathname: string,
 * searchParams?: URLSearchParams | string | Record<string, string>,
 * agentSeoOverride?: object,
 * origin?: string,
 * runtimeRules?: { globalRules: Array<object>, templateRules: Array<object> }
 * }} input
 */
export function classifySeoRoute({ pathname, searchParams, agentSeoOverride, origin, runtimeRules }) {
  const resolvedRules = resolveRuleSets(runtimeRules);
  const normalizedPathname = normalizePathname(pathname);
  const lockedRule = findFirstRuleMatch(resolvedRules.globalRules, normalizedPathname);

  if (lockedRule) {
    return {
      ...lockedRule,
      canonicalUrl: normalizedPathname,
      title: undefined,
      description: undefined,
      robotsContent: toRobotsContent(lockedRule),
    };
  }

  const templateRule = findFirstRuleMatch(resolvedRules.templateRules, normalizedPathname);
  const baseRule = templateRule
    ? { locked: false, ...templateRule }
    : { ...DEFAULT_UNKNOWN_SEO_RULE };

  if (!templateRule && import.meta?.env?.DEV) {
    console.warn(`[SEO Guard] Unknown route classification for "${normalizedPathname}". Using safe default.`);
  }

  const safeOverride = templateRule
    ? sanitizeAgentSeoOverride(templateRule, agentSeoOverride, origin)
    : {};

  const result = {
    ...baseRule,
    ...safeOverride,
    canonicalUrl: normalizeCanonicalPath(safeOverride.canonicalUrl, origin) || normalizedPathname,
  };

  const hasQuery = hasSearchParams(searchParams);
  if (hasQuery && templateRule && templateRule.index === true && templateRule.sitemap === true) {
    result.index = false;
    result.follow = true;
    result.sitemap = false;
    result.canonicalUrl = normalizedPathname;
  }

  result.robotsContent = toRobotsContent(result);
  return result;
}

